import React, { useMemo, useState } from "react";
import "../styles/SpecificationAccordion.css";
import { specificationData } from "../data/specificationData";
import { motion } from "framer-motion";

/* animations unchanged */
const fadeUp = {
  hidden: { opacity: 0, y: 45, filter: "blur(10px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.9, ease: "easeOut" },
  },
};

const wordContainer = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.12, delayChildren: 0.15 },
  },
};

const wordVariant = {
  hidden: { y: 35, opacity: 0, filter: "blur(6px)" },
  show: {
    y: 0,
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

const line1 = "Crafted with Precision ".split(" ");
const line2 = "and Premium Finishes".split(" ");

const mobileCategories = ["Structure", "Interiors", "Services", "Amenities"];

const categoryById = {
  structure: "Structure",
  walls: "Interiors",
  plastering: "Interiors",
  painting_cladding: "Interiors",
  doors_windows_railings: "Interiors",
  flooring: "Interiors",
  dadoing: "Interiors",
  kitchen: "Interiors",
  cp_sanitary: "Interiors",
  electrical: "Services",
  air_conditioning: "Services",
  telecom: "Services",
  lifts: "Services",
  wsp_stp: "Services",
  ev_charging: "Services",
  security_bms: "Services",
  centralized_billing: "Services",
  centralized_water_supply: "Services",
  power_backup: "Services",
  clubhouse_amenities: "Amenities",
  car_porch: "Amenities",
  main_landscaping_works: "Amenities",
  other_landscape_amenities: "Amenities",
};

function getItemCategory(item) {
  return categoryById[item.id] || "Amenities";
}

function getItemSearchText(item) {
  const pointsText = item.points
    .map((point) => (typeof point === "string" ? point : point.text || ""))
    .join(" ");
  return `${item.title} ${pointsText}`.toLowerCase();
}

export default function SpecificationAccordion() {
  const [activeCategory, setActiveCategory] = useState("Amenities");
  const [searchTerm, setSearchTerm] = useState("");

  const mid = Math.ceil(specificationData.length / 2 - 1);
  const leftData = specificationData.slice(0, mid);
  const rightData = specificationData.slice(mid);

  const firstId = specificationData[0]?.id;
  const mobileData = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();
    return specificationData.filter((item) => {
      if (getItemCategory(item) !== activeCategory) return false;
      if (!normalized) return true;
      return getItemSearchText(item).includes(normalized);
    });
  }, [activeCategory, searchTerm]);

  return (
    <section className="specification-section py-5" id="serene-specifications">
      <div className="container">
        <div className="d-none d-lg-block">
          {/* HEADER */}
          <div className="row mb-5">
            <div className="col-lg-7 d-flex align-items-center mt-4 mt-lg-0">
              <motion.h2
                className="sage-title display-4"
                variants={wordContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ once: false, amount: 0.6 }}
              >
                {line1.map((word, i) => (
                  <motion.span
                    key={`line1-${i}`}
                    variants={wordVariant}
                    style={{ display: "inline-block", marginRight: "0.35em" }}
                  >
                    {word}
                  </motion.span>
                ))}
                <br />
                {line2.map((word, i) => (
                  <motion.span
                    key={`line2-${i}`}
                    variants={wordVariant}
                    style={{ display: "inline-block", marginRight: "0.35em" }}
                  >
                    {word}
                  </motion.span>
                ))}
              </motion.h2>
            </div>

            <div className="col-lg-5 d-flex justify-content-center flex-column">
              <motion.p className="about-text mb-0" variants={fadeUp}>
                Every specification at Serene is thoughtfully selected to reflect superior quality and lasting elegance. From refined materials to meticulous craftsmanship, each detail is designed to elevate comfort and style.
              </motion.p>
            </div>
          </div>

          {/* ACCORDION */}
          <div className="row" id="specAccordion">
            {/* LEFT */}
            <div className="col-lg-6">
              <div className="accordion specification-accordion">
                {leftData.map((item) => {
                  const collapseId = `collapse_${item.id}`;
                  const headingId = `heading_${item.id}`;
                  const isFirst = item.id === firstId;

                  return (
                    <div className="accordion-item spec-item mb-3" key={item.id}>
                      <h2 className="accordion-header" id={headingId}>
                        <button
                          className={`accordion-button spec-btn ${!isFirst ? "collapsed" : ""}`}
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target={`#${collapseId}`}
                          aria-expanded={isFirst ? "true" : "false"}
                          aria-controls={collapseId}
                        >
                          <div className="d-flex align-items-center gap-3 w-100">
                            <div className="spec-emoji-box">{item.icon}</div>
                            <span className="fw-medium fs-5">{item.title}</span>
                          </div>

                          <span className="spec-arrow">
                            <span className="arrow">^</span>
                          </span>
                        </button>
                      </h2>

                      <div
                        id={collapseId}
                        className={`accordion-collapse collapse ${isFirst ? "show" : ""}`}
                        aria-labelledby={headingId}
                        data-bs-parent="#specAccordion"
                      >
                        <div className="accordion-body spec-body">
                          {item.points.map((p, i) => {
                            if (typeof p === "string") {
                              return <div className="spec-row" key={i}>{p}</div>;
                            }
                            if (p.type === "sub") {
                              return <div className="spec-row fw-semibold" key={i}>{p.text}</div>;
                            }
                            if (p.type === "space") {
                              return <div className="spec-row py-2 border-0" key={i}></div>;
                            }
                            return <div className="spec-row" key={i}>{p.text}</div>;
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* RIGHT */}
            <div className="col-lg-6">
              <div className="accordion specification-accordion">
                {rightData.map((item) => {
                  const collapseId = `collapse_${item.id}`;
                  const headingId = `heading_${item.id}`;

                  return (
                    <div className="accordion-item spec-item mb-3" key={item.id}>
                      <h2 className="accordion-header" id={headingId}>
                        <button
                          className="accordion-button collapsed spec-btn"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target={`#${collapseId}`}
                          aria-expanded="false"
                          aria-controls={collapseId}
                        >
                          <div className="d-flex align-items-center gap-3 w-100">
                            <div className="spec-emoji-box">{item.icon}</div>
                            <span className="fw-medium fs-5">{item.title}</span>
                          </div>

                          <span className="spec-arrow">
                            <span className="arrow">^</span>
                          </span>
                        </button>
                      </h2>

                      <div
                        id={collapseId}
                        className="accordion-collapse collapse"
                        aria-labelledby={headingId}
                        data-bs-parent="#specAccordion"
                      >
                        <div className="accordion-body spec-body">
                          {item.points.map((p, i) => {
                            if (typeof p === "string") {
                              return <div className="spec-row" key={i}>{p}</div>;
                            }
                            if (p.type === "sub") {
                              return <div className="spec-row fw-semibold" key={i}>{p.text}</div>;
                            }
                            if (p.type === "space") {
                              return <div className="spec-row py-2 border-0" key={i}></div>;
                            }
                            return <div className="spec-row" key={i}>{p.text}</div>;
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="spec-mobile d-lg-none">
          <div className="spec-mobile-head">
            <h3 className="spec-mobile-title">Specifications</h3>
          </div>

          <div className="spec-mobile-tabs" role="tablist" aria-label="Specification categories">
            {mobileCategories.map((category) => (
              <button
                key={category}
                type="button"
                className={`spec-mobile-tab ${activeCategory === category ? "active" : ""}`}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>

          {/* <div className="spec-mobile-search-wrap">
            <span className="spec-mobile-search-icon" aria-hidden="true">{"\u2315"}</span>
            <input
              type="search"
              className="spec-mobile-search"
              placeholder="Search specifications..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              aria-label="Search specifications"
            />
          </div> */}

          <div className="spec-mobile-section-title">
            {activeCategory} Specifications &middot; {mobileData.length}
          </div>

          <div className="accordion specification-accordion spec-mobile-accordion" id="specAccordionMobile">
            {mobileData.map((item) => {
              const collapseId = `mobile_collapse_${item.id}`;
              const headingId = `mobile_heading_${item.id}`;

              return (
                <div className="accordion-item spec-item spec-mobile-item mb-2" key={item.id}>
                  <h2 className="accordion-header" id={headingId}>
                    <button
                      className="accordion-button spec-btn spec-mobile-btn collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target={`#${collapseId}`}
                      aria-expanded="false"
                      aria-controls={collapseId}
                    >
                      <div className="d-flex align-items-center gap-2 w-100">
                        <div className="spec-emoji-box spec-mobile-emoji">{item.icon}</div>
                        <span className="spec-mobile-item-title">{item.title}</span>
                      </div>
                      <span className="spec-arrow spec-mobile-arrow">
                        <span className="arrow">^</span>
                      </span>
                    </button>
                  </h2>

                  <div
                    id={collapseId}
                    className="accordion-collapse collapse"
                    aria-labelledby={headingId}
                    data-bs-parent="#specAccordionMobile"
                  >
                    <div className="accordion-body spec-body">
                      {item.points.map((p, i) => {
                        if (typeof p === "string") {
                          return <div className="spec-row spec-mobile-row" key={i}>{p}</div>;
                        }
                        if (p.type === "sub") {
                          return <div className="spec-row spec-mobile-row fw-semibold" key={i}>{p.text}</div>;
                        }
                        if (p.type === "space") {
                          return <div className="spec-row spec-mobile-row py-2 border-0" key={i}></div>;
                        }
                        return <div className="spec-row spec-mobile-row" key={i}>{p.text}</div>;
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {mobileData.length === 0 && (
            <div className="spec-mobile-empty">No specifications match your search.</div>
          )}
        </div>
      </div>
    </section>
  );
}
